import { useState, useEffect, useCallback } from 'react';
import { createContainer } from 'unstated-next';

const GEO_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  maximumAge: 0,
  timeout: 10000,
};

function useLocation() {
  const [coords, setCoords] = useState<[number, number] | null>(null);
  const [accuracy, setAccuracy] = useState<number | null>(null);
  const [permissionState, setPermissionState] = useState<PermissionState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchPosition = useCallback(
    (isRetry: boolean) => {
      navigator.geolocation.getCurrentPosition(
        position => {
          setCoords([position.coords.longitude, position.coords.latitude]);
          setAccuracy(position.coords.accuracy);
          setLoading(false);
          setPermissionState('granted');
        },
        err => {
          if (err.code === 3 && !isRetry) {
            fetchPosition(true);
            return;
          }
          if (err.code === 1) {
            setError('Location access denied.');
            setPermissionState('denied');
          } else {
            setError('Unable to retrieve your location.');
          }
          setLoading(false);
        },
        GEO_OPTIONS,
      );
    },
    [],
  );

  const requestLocation = useCallback(() => {
    setError(null);
    setLoading(true);
    fetchPosition(false);
  }, [fetchPosition]);

  useEffect(() => {
    if (!navigator.permissions) {
      return;
    }

    let permStatus: PermissionStatus | undefined;

    const handleChange = () => {
      if (permStatus) {
        setPermissionState(permStatus.state);
        if (permStatus.state === 'granted') {
          requestLocation();
        } else {
          setCoords(null);
          setAccuracy(null);
        }
      }
    };

    navigator.permissions
      .query({ name: 'geolocation' })
      .then(status => {
        permStatus = status;
        setPermissionState(status.state);
        status.addEventListener('change', handleChange);
        if (status.state === 'granted') {
          requestLocation();
        }
      });

    return () => {
      if (permStatus) {
        permStatus.removeEventListener('change', handleChange);
      }
    };
  }, [requestLocation]);

  return { coords, accuracy, permissionState, error, loading, requestLocation };
}

export const LocationProvider = createContainer(useLocation);
